/** Reading Foundry Rooms into numbers the rest of the sim uses (GDD §6). */

import { ROOMS, ROOM_BUILD_COSTS, ROOM_OFFERS, roomLevelCost, roomValue, type RoomEffect } from '../content/rooms';
import { resource, type Ingredient } from '../content/resources';
import { projectDef } from '../content/projects';
import type { GameState } from './state';

const BY_EFFECT = new Map(ROOMS.map((r) => [r.effect, r]));

export function roomLevel(state: GameState, id: string): number {
  return state.rooms[id] ?? 0;
}

/** ×multiplier from a multiplier room (1 when unbuilt). */
export function roomMult(state: GameState, effect: RoomEffect): number {
  const def = BY_EFFECT.get(effect);
  return def && def.shape === 'mult' ? roomValue(def, roomLevel(state, def.id)) : 1;
}

/** Fraction cut by a reduction room (0 when unbuilt). */
export function roomCut(state: GameState, effect: RoomEffect): number {
  const def = BY_EFFECT.get(effect);
  return def && def.shape === 'cut' ? roomValue(def, roomLevel(state, def.id)) : 0;
}

export function offlineHours(state: GameState): number {
  const def = BY_EFFECT.get('offline')!;
  return roomValue(def, roomLevel(state, def.id));
}

export function roomsBuilt(state: GameState): number {
  return ROOMS.filter((r) => roomLevel(state, r.id) > 0).length;
}

/** IPM offers the first two unbuilt rooms, in order. */
export function offeredRooms(state: GameState): string[] {
  return ROOMS.filter((r) => roomLevel(state, r.id) === 0).slice(0, ROOM_OFFERS).map((r) => r.id);
}

/** TE for the room's next step: building it (if offered) or its next level. Null if unavailable or maxed. */
export function nextRoomCost(state: GameState, id: string): number | null {
  const def = ROOMS.find((r) => r.id === id);
  if (!def) return null;
  const level = roomLevel(state, id);
  if (level === 0) return offeredRooms(state).includes(id) ? ROOM_BUILD_COSTS[roomsBuilt(state)] ?? null : null;
  return roomLevelCost(def, level);
}

export function upgradeRoom(state: GameState, id: string): boolean {
  const cost = nextRoomCost(state, id);
  if (cost === null || state.te < cost) return false;
  state.te -= cost;
  state.rooms[id] = roomLevel(state, id) + 1;
  return true;
}

/** Scales ingredient counts by a reduction, never below one of each. */
export function cutInputs(inputs: readonly Ingredient[], cutBy: number): Ingredient[] {
  return inputs.map((i) => ({ id: i.id, qty: Math.max(1, Math.ceil(i.qty * (1 - cutBy))) }));
}

/** Project efficiency for a workshop, read directly so rooms.ts needs no import from projects.ts. */
function efficiency(state: GameState, target: 'smelter' | 'crafter'): number {
  let cut = 0;
  for (const id of state.run.projects) {
    for (const e of projectDef(id).effects) if (e.kind === 'efficiency' && e.target === target) cut += e.amount;
  }
  return cut;
}

/** A recipe's inputs after the Underforge/Dorms room and the Efficiency projects (multiplicative). */
export function recipeInputs(state: GameState, id: string): Ingredient[] {
  const def = resource(id);
  const smelt = def.kind === 'alloy';
  const room = roomCut(state, smelt ? 'smeltInputs' : 'craftInputs');
  const project = efficiency(state, smelt ? 'smelter' : 'crafter');
  return cutInputs(def.recipe!.inputs, 1 - (1 - room) * (1 - project));
}
